import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, forkJoin, map, of, switchMap } from "rxjs";
import { LocationChargePointSummary } from "@app/models/chargeprice/charge-point-overview.model";
import { PaginatedList } from "@app/models/common/paginated-list.class";
import { environment } from "@environments/environment";

@Injectable()
export class ChargePriceService {

  /** Largest page size accepted by the charge price service */
  static readonly maxPageSize = 1000;

  private uri: string = `${environment.routes.apiEndpoint}/charge-price/v1`;

  constructor(private http: HttpClient) { }

  /** Summaries are sorted by severity level (descending), then by location name */
  getChargePointSummaries(
    locationIds: string[] = [],
    pageIndex: number = 1,
    pageSize: number = ChargePriceService.maxPageSize
  ) {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    locationIds.forEach(id => params = params.append('locationIds', id));

    return this.http.get<PaginatedList<LocationChargePointSummary>>(`${this.uri}/charge-points/summaries`, { params });
  }

  /** Fetches every page of charge point summaries */
  getAllChargePointSummaries(locationIds: string[] = []): Observable<LocationChargePointSummary[]> {
    return this.getChargePointSummaries(locationIds, 1).pipe(
      switchMap(firstPage => {
        if (firstPage.totalPages <= 1) return of(firstPage.items ?? []);

        const remainingPages = Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
          this.getChargePointSummaries(locationIds, index + 2));

        return forkJoin(remainingPages).pipe(
          map(pages => [firstPage, ...pages].flatMap(page => page.items ?? []))
        );
      })
    );
  }
}